import type { EnvironmentConfig, FlightState, SwingParams, Vec3 } from '../types';
import { computeAirDensity, DEFAULT_ENVIRONMENT, DEG_TO_RAD, MPH_TO_MS } from '../constants';
import { computeLaunchState } from '../simulation/SwingInput';
import { stepFlight } from './BallFlightModel';
import { interpolateGroundContact } from './GroundContact';
import { getAeroCorrection, launchSpinFactor } from './ResidualCorrection';
import type { AeroCorrection } from './ResidualCorrection';

export type FlightModel = 'base' | 'legacy-corrected';
export interface FlightContext {
  wind: Vec3;
  airDensity: number;
  correction?: AeroCorrection;
}

/** One atmosphere conversion for the live shot, comparisons, and benchmarks. */
export function flightEnvironment(environment: EnvironmentConfig): FlightContext {
  const speed = environment.windSpeed * MPH_TO_MS;
  const angle = environment.windDirection * DEG_TO_RAD;
  return {
    airDensity: computeAirDensity(environment.altitude, environment.temperature),
    wind: { x: speed * Math.sin(angle), y: 0, z: -speed * Math.cos(angle) },
  };
}

/** Corrections are explicit and frozen at launch, never silently different by caller.
 * The new calibration experiment is not promoted: wedge cohort gates still fail. */
export function createFlightSetup(params: SwingParams, environment = DEFAULT_ENVIRONMENT,
  model: FlightModel = 'base', mode: 'measured' | 'estimated' = 'measured',
): { state: FlightState; context: FlightContext } {
  const state = computeLaunchState(params, mode);
  const context = flightEnvironment(environment);
  if (model === 'legacy-corrected') {
    const speed = Math.hypot(state.velocity.x, state.velocity.y, state.velocity.z);
    context.correction = getAeroCorrection(launchSpinFactor(state.spinRate, speed));
  }
  return { state, context };
}

export function advanceShotFlight(state: FlightState, dt: number, context: FlightContext) {
  const next = stepFlight(state, dt, context.wind, context.airDensity, context.correction);
  const landing = interpolateGroundContact(state, next);
  return { state: landing ?? next, landed: landing !== null };
}
